import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Sku, UpdateSkuInput } from '../../../types';

interface Props {
  sku: Sku;
  onSave: (patch: Partial<UpdateSkuInput>) => void;
}

function ExternalIdField({
  id,
  label,
  hint,
  value,
  onCommit,
  placeholder,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  onCommit: (next: string) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState(value);
  const baselineRef = useRef(value);

  useEffect(() => {
    setDraft(value);
    baselineRef.current = value;
  }, [value]);

  const handleBlur = () => {
    if (draft === baselineRef.current) return;
    baselineRef.current = draft;
    onCommit(draft);
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </Label>
      <Input
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="font-mono"
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export function SkuAccountingTab({ sku, onSave }: Props) {
  return (
    <div className="space-y-5">
      <ExternalIdField
        id="qbo-id"
        label="QuickBooks Item ID"
        hint="Numeric ID of the matching Item in QuickBooks Online (qb.atek.io)."
        placeholder="e.g. 142"
        value={sku.quickbookId ?? ''}
        onCommit={(quickbookId) => onSave({ quickbookId })}
      />

      <ExternalIdField
        id="medusa-id"
        label="Medusa Product ID"
        hint="Medusa storefront product handle/ID (e.g. prod_01HQ…). Set once when the product is first published to avoid duplicates online."
        placeholder="prod_…"
        value={sku.medusaId ?? ''}
        onCommit={(medusaId) => onSave({ medusaId })}
      />

      <ExternalIdField
        id="hubspot-id"
        label="HubSpot Product ID"
        hint="HubSpot CRM product object ID (used in deal line items)."
        placeholder="e.g. 1234567890"
        value={sku.hubspotId ?? ''}
        onCommit={(hubspotId) => onSave({ hubspotId })}
      />

      <p className="text-xs text-muted-foreground border-t pt-3">
        Coming next: Search buttons to look up these IDs in each system, plus a
        Compare tab that shows ATEK / QBO / Medusa / HubSpot side-by-side.
      </p>
    </div>
  );
}
